import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { cartApi } from '@/api/orders'
import type { DeliverySettings, ProductUnit } from '@/api/suppliers'
import { UNITS, suppliersApi } from '@/api/suppliers'
import { AppShell, ConnectionBadge } from '@/components/AppShell'
import { toast } from '@/lib/toast'
import { IconBox, IconCart, IconStar, IconTrash } from '@/components/icons'
import { Alert, BackLink, Button, EmptyState, Field, SkeletonList } from '@/components/ui'
import { useApiError } from '@/features/auth/useApiError'
import { DeliverySettingsForm, DeliverySummary } from '@/features/delivery/DeliveryWindow'
import { formatDate } from '@/lib/format'
import { CatalogPicker } from '@/features/suppliers/CatalogPicker'
import { useAuthStore } from '@/stores/authStore'


export default function SupplierDetailPage() {
  const { t } = useTranslation()
  const { supplierId = '' } = useParams()
  const queryClient = useQueryClient()
  const translateError = useApiError()
  const canManageItems = useAuthStore((s) => s.hasPermission('item:manage'))
  const [mode, setMode] = useState<'none' | 'catalog' | 'custom'>('none')
  const [editingDelivery, setEditingDelivery] = useState(false)

  const supplier = useQuery({
    queryKey: ['supplier', supplierId],
    queryFn: () => suppliersApi.get(supplierId),
  })
  const items = useQuery({
    queryKey: ['supplier-items', supplierId],
    queryFn: () => suppliersApi.items(supplierId),
  })

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['supplier-items', supplierId] })
    void queryClient.invalidateQueries({ queryKey: ['supplier', supplierId] })
    void queryClient.invalidateQueries({ queryKey: ['suppliers'] })
  }

  const toggleFavorite = useMutation({
    mutationFn: ({ id, isFavorite }: { id: string; isFavorite: boolean }) =>
      suppliersApi.updateItem(id, { isFavorite }),
    onSuccess: invalidate,
  })
  const removeItem = useMutation({
    mutationFn: suppliersApi.deleteItem,
    onSuccess: invalidate,
  })
  const acceptConnection = useMutation({
    mutationFn: suppliersApi.acceptConnection,
    onSuccess: invalidate,
  })
  const saveDelivery = useMutation({
    mutationFn: (settings: DeliverySettings) => suppliersApi.update(supplierId, settings),
    onSuccess: () => {
      setEditingDelivery(false)
      invalidate()
      toast.success(t('common.saved'))
    },
  })
  const addToCart = useMutation({
    mutationFn: (item: { id: string; quantity: string }) =>
      cartApi.addItem({
        supplierId,
        businessItemId: item.id,
        quantity: item.quantity,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['cart'] })
      toast.success(t('items.addedToCart'))
    },
  })

  return (
    <AppShell title={supplier.data?.name ?? t('common.loading')}>
      <BackLink to="/suppliers" label={t('common.back')} />

      {supplier.data && (
        <section className="mb-4 rounded-2xl border border-gray-200 bg-white p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate text-sm text-gray-500">{supplier.data.email}</p>
              {supplier.data.phone && (
                <p className="text-sm text-gray-500">{supplier.data.phone}</p>
              )}
            </div>
            <ConnectionBadge status={supplier.data.connectionStatus} />
          </div>

          {supplier.data.connectionStatus === 'pending_customer' &&
            supplier.data.connectionId && (
              <div className="mt-3">
                <p className="mb-2 text-sm text-gray-600">{t('connection.awaitingYou')}</p>
                <Button
                  loading={acceptConnection.isPending}
                  onClick={() => acceptConnection.mutate(supplier.data!.connectionId!)}
                >
                  {t('connection.accept')}
                </Button>
              </div>
            )}

          {supplier.data.state === 'shadow' && (
            <p className="mt-3 text-xs text-gray-500">{t('suppliers.shadowHint')}</p>
          )}
        </section>
      )}

      {supplier.data?.delivery && !editingDelivery && (
        <section className="mb-4 rounded-2xl border border-gray-200 bg-white p-4">
          <h2 className="mb-2 font-semibold text-gray-900">{t('delivery.title')}</h2>
          <DeliverySummary window={supplier.data.delivery} />

          {/* Μόλις ο προμηθευτής αποκτήσει λογαριασμό ορίζει μόνος του το
              ωράριό του: ο πελάτης δεν ξέρει αν σήμερα έκλεισε νωρίτερα. */}
          {supplier.data.state === 'shadow' && canManageItems ? (
            <Button
              variant="secondary"
              className="mt-3"
              onClick={() => setEditingDelivery(true)}
            >
              {t('delivery.edit')}
            </Button>
          ) : (
            <p className="mt-3 text-xs text-gray-500">{t('delivery.managedBySupplier')}</p>
          )}
        </section>
      )}

      {editingDelivery && (
        <div className="mb-4">
          <DeliverySettingsForm
            window={supplier.data?.delivery ?? null}
            saving={saveDelivery.isPending}
            onSave={(settings) => saveDelivery.mutate(settings)}
          >
            {saveDelivery.isError && <Alert>{translateError(saveDelivery.error)}</Alert>}
            <Button variant="secondary" type="button" onClick={() => setEditingDelivery(false)}>
              {t('common.cancel')}
            </Button>
          </DeliverySettingsForm>
        </div>
      )}

      {canManageItems && mode === 'none' && (
        <div className="mb-4 flex flex-col gap-2">
          {/* Ο κατάλογος πρώτος: είναι η γρήγορη διαδρομή για τους περισσότερους. */}
          <Button onClick={() => setMode('catalog')}>{t('catalog.open')}</Button>
          <Button variant="secondary" onClick={() => setMode('custom')}>
            {t('items.addCustom')}
          </Button>
        </div>
      )}

      {canManageItems && mode === 'catalog' && (
        <CatalogPicker
          supplierId={supplierId}
          existingBaseProductIds={
            new Set((items.data ?? []).map((i) => i.baseProductId).filter((id): id is string => Boolean(id)))
          }
          onDone={() => {
            invalidate()
            setMode('none')
          }}
        />
      )}

      {canManageItems && mode === 'custom' && (
        <AddItemForm
          supplierId={supplierId}
          onDone={() => {
            invalidate()
            setMode('none')
          }}
        />
      )}

      <h2 className="mb-2 mt-4 text-sm font-medium uppercase tracking-wide text-gray-500">
        {t('items.title')}
      </h2>

      {items.isPending && <SkeletonList rows={4} />}
      {items.isError && <Alert>{translateError(items.error)}</Alert>}

      {items.data?.length === 0 && (
        <EmptyState
          icon={<IconBox size={26} />}
          title={t('items.empty')}
          description={t('items.emptyHint')}
        />
      )}

      <ul className="flex flex-col gap-2">
        {items.data?.map((item) => (
          <li
            key={item.id}
            className="flex items-center justify-between gap-3 rounded-2xl border border-gray-200 bg-white p-4"
          >
            <div className="min-w-0">
              <p className="truncate font-medium text-gray-900">{item.name}</p>
              <p className="text-sm text-gray-500">
                {t('items.defaultQuantity')}: {formatQuantity(item.defaultQuantity)}{' '}
                {t(`units.${item.unit}`)}
              </p>
              {/* Η έλλειψη φαίνεται εδώ, τη στιγμή της επιλογής: μετά την
                  αποστολή δεν προλαβαίνει να καλυφθεί από αλλού. */}
              {!item.isAvailableToday && (
                <p className="mt-1 inline-flex items-center rounded-lg bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800">
                  {item.unavailableUntil
                    ? t('availability.until', { date: formatDate(item.unavailableUntil) })
                    : t('availability.missingToday')}
                </p>
              )}
            </div>

            <div className="flex shrink-0 items-center gap-1">
              {/* Ελάχιστα βήματα από τη λίστα ειδών ως την αποστολή (§11) */}
              <button
                onClick={() =>
                  addToCart.mutate({ id: item.id, quantity: item.defaultQuantity })
                }
                className="flex items-center gap-1.5 rounded-lg bg-brand-50 px-3 py-2 text-sm font-semibold text-brand-700 hover:bg-brand-100"
              >
                <IconCart size={16} />
                {t('items.addToCart')}
              </button>

              {canManageItems && (
                <>
                  <button
                    aria-label={t('items.toggleFavorite')}
                    onClick={() =>
                      toggleFavorite.mutate({ id: item.id, isFavorite: !item.isFavorite })
                    }
                    className={
                      'rounded-lg px-2 py-2 transition-colors ' +
                      (item.isFavorite ? 'text-amber-500' : 'text-gray-300 hover:text-gray-500')
                    }
                  >
                    <IconStar size={19} filled={item.isFavorite} />
                  </button>
                  <button
                    aria-label={t('common.delete')}
                    onClick={() => removeItem.mutate(item.id)}
                    className="rounded-lg px-2 py-2 text-gray-400 transition-colors hover:text-red-600"
                  >
                    <IconTrash size={18} />
                  </button>
                </>
              )}
            </div>
          </li>
        ))}
      </ul>
    </AppShell>
  )
}

/** Οι ποσότητες έρχονται ως δεκαδικό κείμενο· τα περιττά μηδενικά κρύβονται. */
function formatQuantity(value: string): string {
  const number = Number(value)
  return Number.isFinite(number) ? new Intl.NumberFormat('el-GR').format(number) : value
}

function AddItemForm({ supplierId, onDone }: { supplierId: string; onDone: () => void }) {
  const { t } = useTranslation()
  const translateError = useApiError()
  const [name, setName] = useState('')
  const [unit, setUnit] = useState<ProductUnit>('piece')
  const [quantity, setQuantity] = useState('1')

  const mutation = useMutation({
    mutationFn: () =>
      suppliersApi.createItem(supplierId, {
        customName: name.trim(),
        unit,
        defaultQuantity: quantity || '1',
      }),
    onSuccess: () => {
      setName('')
      setQuantity('1')
      onDone()
    },
  })

  return (
    <form
      className="flex flex-col gap-3 rounded-2xl border border-gray-200 bg-white p-4"
      onSubmit={(event) => {
        event.preventDefault()
        if (name.trim()) mutation.mutate()
      }}
    >
      <h2 className="font-semibold text-gray-900">{t('items.add')}</h2>

      {mutation.isError && <Alert>{translateError(mutation.error)}</Alert>}

      <Field
        label={t('items.fields.name')}
        name="itemName"
        value={name}
        onChange={(event) => setName(event.target.value)}
      />

      <div className="flex gap-3">
        <div className="flex-1">
          <Field
            label={t('items.fields.defaultQuantity')}
            name="itemQuantity"
            type="number"
            step="0.001"
            min="0.001"
            inputMode="decimal"
            value={quantity}
            onChange={(event) => setQuantity(event.target.value)}
          />
        </div>
        <div className="flex flex-1 flex-col gap-1.5">
          <label htmlFor="itemUnit" className="text-sm font-medium text-gray-700">
            {t('items.fields.unit')}
          </label>
          <select
            id="itemUnit"
            value={unit}
            onChange={(event) => setUnit(event.target.value as ProductUnit)}
            className="h-touch rounded-xl border border-gray-300 bg-white px-3 text-base text-gray-900 outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-100"
          >
            {UNITS.map((value) => (
              <option key={value} value={value}>
                {t(`units.${value}`)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex gap-2">
        <Button type="submit" loading={mutation.isPending} disabled={!name.trim()}>
          {t('items.add')}
        </Button>
        <Button type="button" variant="secondary" onClick={onDone}>
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  )
}
